'use client';

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Droplets, Activity, AlertTriangle, MapPin,
  ChevronRight, Eye, CheckCircle,
  XCircle, RefreshCw,
  BarChart3, Settings, Sparkles
} from "lucide-react";
import { ref, onValue, set } from "firebase/database";
import { database } from "../../config/firebase";
import notificationService from "../../services/NotificationService";
import ValidationAlert from "../../components/ValidationAlert";
import AnimatedNumber from "../../components/AnimatedNumber";
// import SkeletonLoader from "../../components/SkeletonLoader"; // Future: Add to loading states

// Toast notification component (inline since react-hot-toast not installed)
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? '#16A34A' : type === 'error' ? '#DC2626' : '#00cf45bc';

  return (
    <div style={{
      position: 'fixed',
      top: '24px',
      right: '24px',
      background: bgColor,
      color: 'white',
      padding: '16px 24px',
      borderRadius: '12px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      animation: 'slideIn 0.3s ease',
      minWidth: '300px'
    }}>
      {type === 'success' && <CheckCircle size={20} />}
      {type === 'error' && <XCircle size={20} />}
      <span style={{ fontSize: '14px', fontWeight: 600 }}>{message}</span>
    </div>
  );
};

const DashboardPage = () => {
  const [nodes, setNodes] = useState([]);
  const [stats, setStats] = useState({
    totalNodes: 0,
    activeNodes: 0,
    alerts: 0,
    notActivated: 0,
    totalWaterHeight: 0,
    avgWaterHeight: 0
  });
  const [loading, setLoading] = useState(true);
  const [activationModal, setActivationModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [cableLength, setCableLength] = useState("");
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Dynamic greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    const name = "WISE Future"; // You can get this from currentUser if available

    if (hour >= 0 && hour < 6) {
      return {
        text: `Still awake, ${name}? 🌙`,
        subtext: "Late night monitoring session"
      };
    } else if (hour >= 6 && hour < 12) {
      return {
        text: `Good morning, ${name}! ☀️`,
        subtext: "Ready to start the day"
      };
    } else if (hour >= 12 && hour < 17) {
      return {
        text: `Good afternoon, ${name}! 👋`,
        subtext: "Hope you're having a productive day"
      };
    } else if (hour >= 17 && hour < 21) {
      return {
        text: `Good evening, ${name}!🌞`,
        subtext: "Winding down for the day"
      };
    } else {
      return {
        text: `Good night, ${name}! 🌙`,
        subtext: "Late evening check-in"
      };
    }
  };

  const greeting = getGreeting();

  // Fetch all data from Firebase
  useEffect(() => {
    console.log("🚀 Starting Firebase connection...");

    // Initialize notification service
    notificationService.init();

    const rootRef = ref(database);

    const unsubscribe = onValue(rootRef, (snapshot) => {
      console.log("📡 Firebase snapshot received");
      setLastUpdate(new Date());

      if (snapshot.exists()) {
        const data = snapshot.val();
        console.log("📊 Database structure:", Object.keys(data));



        // Process LoRaSensor data to extract nodes
        if (data.LoRaSensor && data.config && data.config.nodes) {
          const nodeKeys = Object.keys(data.LoRaSensor);
          const nodesArray = [];

          nodeKeys.forEach(nodeKey => {
            const nodeData = data.LoRaSensor[nodeKey];
            const nodeConfig = data.config.nodes[nodeKey];

            // Get the latest reading
            const timestamps = Object.keys(nodeData).sort().reverse();
            const latestTimestamp = timestamps[0];
            const latestReading = nodeData[latestTimestamp];

            // Update notification service with latest timestamp
            notificationService.updateNodeTimestamp(nodeKey, latestTimestamp);

            // Parse RawData string or get direct depth_m
            let depth_m = 0;
            const reading = latestReading;

            // 1. Try checking direct numeric fields
            if (reading.depth_m !== undefined) depth_m = parseFloat(reading.depth_m);
            else if (reading.Depth !== undefined) depth_m = parseFloat(reading.Depth);
            else if (reading.depth !== undefined) depth_m = parseFloat(reading.depth);
            else if (reading.H2 !== undefined) depth_m = parseFloat(reading.H2);
            else if (reading.h2 !== undefined) depth_m = parseFloat(reading.h2);

            // 2. If still 0 (or undefined), try RawData string parsing
            if ((!depth_m || depth_m === 0) && reading.RawData && typeof reading.RawData === 'string') {
              let depthMatch = reading.RawData.match(/Depth\s*[=:]\s*([\d.]+)/i);
              if (!depthMatch) {
                depthMatch = reading.RawData.match(/D\s*[=:]\s*([\d.]+)/i);
              }
              if (depthMatch) depth_m = parseFloat(depthMatch[1]);
            }

            // Check if node is activated from config.nodes
            const isActivated = nodeConfig ? nodeConfig.activated : false;
            const h1_m = nodeConfig ? nodeConfig.h1_m : 0;

            // Calculate water height: Correct Formula
            // h1_m = Cable length (from surface to cable end)
            // h2_m = Sensor depth reading (from surface to sensor)
            // Water Height = h2_m - h1_m (water above cable end)
            // If h2 > h1, sensor is deeper than cable = ERROR/NO WATER

            // If h2 > h1, sensor is deeper than cable = ERROR/NO WATER

            const h2_m = depth_m; // Sensor reading (depth from surface)

            // Parse Timestamp
            let parsedDate = new Date(latestTimestamp);
            if (isNaN(parsedDate.getTime())) {
              // Handle format: YYYY-MM-DD_HH-mm-ss
              if (latestTimestamp.includes('_') && latestTimestamp.includes('-')) {
                const parts = latestTimestamp.split('_');
                if (parts.length === 2) {
                  const datePart = parts[0];
                  const timePart = parts[1].replace(/-/g, ':');
                  const isoString = `${datePart}T${timePart}`;
                  parsedDate = new Date(isoString);
                }
              }
              // Fallback / checks
              if (isNaN(parsedDate.getTime()) && !isNaN(parseInt(latestTimestamp))) {
                parsedDate = new Date(parseInt(latestTimestamp));
              }
            }
            let waterHeight = 0;
            let status = "Not Activated";

            if (h1_m > 0) {
              // Correct calculation: Water Height = Sensor Depth - Cable Length
              waterHeight = h1_m - h2_m;

              // Determine status only if activated
              if (isActivated) {
                if (h2_m >= h1_m) {
                  status = "Critical";
                } else if (waterHeight > 10) {
                  status = "Active";
                } else if (waterHeight >= 5) {
                  status = "Warning";
                } else if (waterHeight > 0) {
                  status = "Low";
                } else {
                  status = "Critical";
                }
              }
            }

            console.log(`${nodeKey}: h1=${h1_m}m, h2=${h2_m}m, waterHeight=${waterHeight}m, status=${status}`);

            nodesArray.push({
              id: nodeKey,
              name: nodeKey,
              activated: isActivated,
              h1_m: h1_m,
              h2_m: h2_m,
              waterHeight: parseFloat(waterHeight.toFixed(2)),
              status: status,
              location: "Tanzania",
              region: "Arusha",
              depth_m: depth_m,
              lastUpdate: latestTimestamp,
              lastUpdateDate: parsedDate,
              readingsCount: timestamps.length
            });
          });

          setNodes(nodesArray);
          calculateStats(nodesArray);
          setLastUpdate(new Date());
        }
      }
      setLoading(false);
    }, (error) => {
      console.error("❌ Firebase error:", error);
      setLoading(false);
    });

    return () => {
      unsubscribe();
      // Don't destroy notification service - let it continue monitoring
      // notificationService.destroy();
    };
  }, []);

  // Calculate statistics
  const calculateStats = (nodesArray) => {
    const total = nodesArray.length;
    const now = new Date();
    const twoHoursAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);

    // A node is "Active" if it has sent data within the last 2 hours
    const active = nodesArray.filter(n =>
      n.activated && n.lastUpdateDate instanceof Date && !isNaN(n.lastUpdateDate) && n.lastUpdateDate >= twoHoursAgo
    ).length;

    const notActivated = nodesArray.filter(n => !n.activated).length;
    const alerts = nodesArray.filter(n =>
      n.status === "Warning"
    ).length;

    const activatedNodes = nodesArray.filter(n => n.activated);
    const totalWaterHeight = activatedNodes.reduce((sum, n) => sum + n.waterHeight, 0);
    const avgWaterHeight = activatedNodes.length > 0 ? totalWaterHeight / activatedNodes.length : 0;

    setStats({
      totalNodes: total,
      activeNodes: active,
      alerts: alerts,
      notActivated: notActivated,
      totalWaterHeight: parseFloat(totalWaterHeight.toFixed(2)),
      avgWaterHeight: parseFloat(avgWaterHeight.toFixed(2))
    });
  };

  // Activate node
  const handleActivateNode = async () => {
    if (!cableLength || parseFloat(cableLength) <= 0) {
      showToast("Please enter a valid cable length (h₁)", 'error');
      return;
    }

    try {
      const h1_val = parseFloat(cableLength);
      if (isNaN(h1_val)) {
        showToast("Invalid cable length value", 'error');
        return;
      }

      const nodeConfigRef = ref(database, `config/nodes/${activationModal.id}`);
      await set(nodeConfigRef, {
        activated: true,
        h1_m: h1_val
      });

      setActivationModal(null);
      setCableLength("");
      showToast(`${activationModal.name} activated successfully!`, 'success');
    } catch (error) {
      console.error("❌ Error activating node:", error);
      showToast("Failed to activate node. Please try again.", 'error');
    }
  };



  const handleUpdateCableLength = async () => {
    if (!cableLength || parseFloat(cableLength) <= 0) {
      showToast("Please enter a valid cable length", 'error');
      return;
    }

    try {
      const nodeConfigRef = ref(database, `config/nodes/${editModal.id}/h1_m`);
      await set(nodeConfigRef, parseFloat(cableLength));

      setEditModal(null);
      setCableLength("");
      showToast(`Cable length updated for ${editModal.name}`, 'success');
    } catch (error) {
      console.error("❌ Error updating cable length:", error);
      showToast("Failed to update cable length", 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active": return "#16A34A";
      case "Warning": return "#D97706";
      case "Not Activated": return "#999";
      default: return "#999";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Active": return <CheckCircle size={14} />;
      case "Warning": return <AlertTriangle size={14} />;
      default: return <Activity size={14} />;
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #F0F0F0',
          borderTopColor: '#00cf45bc',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }}></div>
        <p style={{ fontSize: '14px', color: '#666', fontWeight: 500 }}>Loading Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <style jsx>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .dashboard-page {
          min-height: 100vh;
          background: #f8fafc;
          padding: 28px 32px;
          font-family: var(--font-primary);
          color: var(--color-text);
          transition: background-color var(--transition-normal);
        }

      /* ===============================
   Glassmorphism Dashboard Header
   =============================== */

.dashboard-header {
  position: relative;
  overflow: hidden;
  background: #ffffff;
  border-radius: 18px;
  padding: 28px;
  margin-bottom: 22px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.08);
}

/* Light refraction highlight */
.dashboard-header::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(120deg, rgba(14, 165, 233, 0.08), transparent 40%, rgba(16, 185, 129, 0.08));
  opacity: 1;
  pointer-events: none;
  z-index: 0;
}

/* Blue accent glow */
.dashboard-header::after {
  content: "";
  position: absolute;
  display: none;
  pointer-events: none;
  z-index: 0;
}

/* Keep header content above glass layers */
.dashboard-header > * {
  position: relative;
  z-index: 1;
}


        .header-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 18px;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-icon {
          width: 52px;
          height: 52px;
          background: linear-gradient(135deg, #0369a1, #0284c7);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 20px rgba(3, 105, 161, 0.25);
        }

        .header-text h1 {
          font-size: 27px;
          font-weight: 900;
          color: #0f172a;
          margin-bottom: 4px;
          letter-spacing: -0.5px;
        }

        .header-text p {
          font-size: 13px;
          color: #64748b;
          font-weight: 700;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .btn-header {
          padding: 10px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
        }

        .btn-primary {
          background: linear-gradient(135deg, #0369a1, #0284c7);
          color: white;
          box-shadow: 0 4px 12px rgba(3, 105, 161, 0.25);
        }

        .btn-primary:hover {
          background: linear-gradient(135deg, #0c4a6e, #0369a1);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(3, 105, 161, 0.35);
        }

        .btn-secondary {
          background: rgba(255, 255, 255, 0.8);
          color: #0369a1;
          border: 2px solid #e2e8f0;
          backdrop-filter: blur(10px);
        }

        .btn-secondary:hover {
          border-color: #0369a1;
          background: white;
        }

     /* ===============================
   Ultra-Modern Glassmorphism Hero
   =============================== */

.hero-card {
  background:
    linear-gradient(135deg, rgba(2, 132, 199, 0.95), rgba(14, 165, 233, 0.82) 48%, rgba(16, 185, 129, 0.78)),
    repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0 1px, transparent 1px 18px);
  border-radius: 18px;
  padding: 34px;
  color: #ffffff;

  position: relative;
  overflow: hidden;

  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 20px 44px rgba(2, 132, 199, 0.22);

  transition: transform 0.45s ease, box-shadow 0.45s ease;
}

/* Hover lift */
.hero-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 26px 52px rgba(2, 132, 199, 0.26);
}

/* Light refraction layer */
.hero-card::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(120deg, rgba(255, 255, 255, 0.22), transparent 44%, rgba(255, 255, 255, 0.12));
  opacity: 1;
  pointer-events: none;
  z-index: 0;
}

/* Aurora glow background */
.hero-card::after {
  content: "";
  position: absolute;
  inset: auto 28px 28px auto;
  background: rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  filter: blur(0);
  height: 140px;
  width: 140px;
  pointer-events: none;
  z-index: 0;
}

/* Aurora animation */
@keyframes aurora {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-30px) scale(1.05);
  }
}

/* Content stays above effects */
.hero-content {
  position: relative;
  z-index: 1;
}

/* ===============================
   Typography
   =============================== */

.hero-label {
  font-size: 12px;
  font-weight: 700;
  opacity: 0.86;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.hero-value {
  font-size: 64px;
  font-weight: 900;
  letter-spacing: -2px;
  line-height: 1;
}

.hero-unit {
  font-size: 22px;
  opacity: 0.85;
  margin-left: 6px;
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin-top: 10px;
}

/* ===============================
   Stats Section
   =============================== */

.hero-stats {
  display: flex;
  gap: 24px;
  margin-top: 28px;
}

.hero-stat {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: none;

  transition: transform 0.35s ease, box-shadow 0.35s ease;
}

.hero-stat:hover {
  transform: translateY(-2px);
  box-shadow: none;
}

.hero-stat-label {
  font-size: 11px;
  opacity: 0.7;
  letter-spacing: 0.5px;
}

.hero-stat-value {
  font-size: 19px;
  font-weight: 800;
}

/* ===============================
   Stats Grid
   =============================== */

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

/* ===============================
   Glass Stat Card
   =============================== */

.stat-card {
  position: relative;
  overflow: hidden;

  background: #ffffff;
  border-radius: 14px;
  padding: 22px;
  border: 1px solid #dbeafe;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);

  transition: transform 0.4s ease, box-shadow 0.4s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.12);
}

/* ===============================
   Blue Accent Line
   =============================== */

.stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 16px;
  right: 16px;
  height: 3px;

  background: linear-gradient(
    90deg,
    #0ea5e9,
    #22d3ee,
    #0284c7
  );

  border-radius: 0 0 6px 6px;
  opacity: 0.95;
}

/* Soft blue glow */
.stat-card::after {
  content: "";
  position: absolute;
  inset: -40%;

  background:
    radial-gradient(circle at 20% 20%, rgba(14, 165, 233, 0.28), transparent 40%),
    radial-gradient(circle at 80% 80%, rgba(34, 211, 238, 0.22), transparent 45%);

  filter: blur(50px);
  pointer-events: none;
}

/* ===============================
   Card Content
   =============================== */

.stat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
}

/* Glass Icon */
.stat-icon {
  width: 48px;
  height: 48px;
  background: #e0f2fe;
  border-radius: 12px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid #bae6fd;
  box-shadow: none;
  color: #0369a1;
}

/* ===============================
   Typography (Blue Ocean System)
   =============================== */

/* Hard-coded text (e.g. "Total Nodes") */
.stat-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;

  color:  #0b0152; /* blue ocean */
  text-shadow: none;

  margin-bottom: 8px;
}

/* Main value (e.g. "4") */
.stat-value {
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.6px;

  color: #0f172a;
  text-shadow: none;
}

/* Subtitle (e.g. "LoRa Sensors") */
.stat-subtitle {
  font-size: 12px;
  font-weight: 600;

  color: #0b0152; /* lighter blue ocean */
  text-shadow: none;

  margin-top: 6px;
}


        /* Section Header */
        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .section-title h2 {
          font-size: 22px;
          font-weight: 900;
          color: #000;
          letter-spacing: -0.3px;
        }

        .section-badge {
          padding: 8px 16px;
          background: #0f172a;
          color: white;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(3, 105, 161, 0.2);
        }

        /* Nodes Grid */
        .nodes-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        .node-card {
          background: #ffffff;
          border-radius: 14px;
          padding: 22px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.07);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .node-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 18px 34px rgba(15, 23, 42, 0.11);
          border-color: #0ea5e9;
        }

        .node-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 20px;
        }

        .node-info h3 {
          font-size: 18px;
          font-weight: 700;
          color: #000;
          margin-bottom: 4px;
          letter-spacing: -0.2px;
        }

        .node-location {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #999;
          font-weight: 500;
        }

        .status-badge {
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        /* Water Display */
        .water-display {
          background: linear-gradient(135deg, #0c4a6e 0%, #0369a1 50%, #0284c7 100%);
          border-radius: 20px;
          padding: 32px;
          margin-bottom: 20px;
          text-align: center;
          position: relative;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(3, 105, 161, 0.25);
        }

        .water-display::before {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 40%;
          background: linear-gradient(0deg, rgba(255,255,255,0.1) 0%, transparent 100%);
          animation: wave 3s ease-in-out infinite;
        }

        @keyframes wave {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        .water-label {
          font-size: 12px;
          color: rgba(255,255,255,0.8);
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 12px;
          position: relative;
          z-index: 1;
        }

        .water-value {
          font-size: 56px;
          font-weight: 800;
          color: white;
          margin-bottom: 12px;
          letter-spacing: -2px;
          text-shadow: 0 4px 12px rgba(0,0,0,0.2);
          position: relative;
          z-index: 1;
        }

        .water-formula {
          font-size: 13px;
          color: white;
          font-weight: 600;
          padding: 10px 20px;
          background: rgba(255,255,255,0.2);
          border-radius: 12px;
          display: inline-block;
          backdrop-filter: blur(5px);
          position: relative;
          z-index: 1;
        }

        /* Metrics Grid */
        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }

        .metric-item {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-radius: 14px;
          padding: 18px;
          text-align: center;
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
        }

        .metric-item:hover {
          border-color: #0ea5e9;
          transform: translateY(-2px);
        }

        .metric-label {
          font-size: 10px;
          color: #999;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          margin-bottom: 8px;
        }

        .metric-value {
          font-size: 20px;
          font-weight: 700;
          color: #000;
          letter-spacing: -0.3px;
        }

        /* Action Buttons */
        .action-buttons {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 16px;
        }

        .btn {
          padding: 12px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          transition: all 0.2s ease;
          border: none;
        }

        .btn-dark {
          background: linear-gradient(135deg, #0369a1, #0284c7);
          color: white;
          box-shadow: 0 4px 12px rgba(3, 105, 161, 0.2);
        }

        .btn-dark:hover {
          background: linear-gradient(135deg, #0c4a6e, #0369a1);
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(3, 105, 161, 0.3);
        }

        .btn-outline {
          background: rgba(255, 255, 255, 0.8);
          color: #0369a1;
          border: 2px solid #e2e8f0;
        }

        .btn-outline:hover {
          border-color: #0ea5e9;
          background: white;
        }

        .last-update {
          font-size: 11px;
          color: #999;
          text-align: center;
          font-weight: 500;
        }

        /* Not Activated State */
        .not-activated {
          padding: 40px 24px;
          text-align: center;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-radius: 16px;
          margin-bottom: 16px;
          border: 2px dashed #cbd5e1;
        }

        .not-activated-icon {
          color: #CCC;
          margin: 0 auto 16px;
        }

        .not-activated-text {
          font-size: 14px;
          color: #666;
          margin-bottom: 4px;
          font-weight: 600;
        }

        .not-activated-count {
          font-size: 12px;
          color: #999;
          margin-bottom: 20px;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 24px;
        }

        .modal-content {
          background: white;
          padding: 32px;
          border-radius: 20px;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          margin-bottom: 24px;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 700;
          color: #000;
          margin-bottom: 8px;
          letter-spacing: -0.5px;
        }

        .modal-subtitle {
          font-size: 14px;
          color: #666;
        }

        .modal-info {
          padding: 20px;
          background: #FAFAFA;
          border-radius: 12px;
          margin-bottom: 24px;
          border: 1px solid #E8E8E8;
        }

        .modal-info p {
          color: #666;
          font-size: 13px;
          line-height: 1.8;
          margin: 0;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 700;
          color: #000;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .form-input {
          width: 100%;
          padding: 14px;
          border: 2px solid #E8E8E8;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          outline: none;
          transition: all 0.2s ease;
        }

        .form-input:focus {
          border-color: #000;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
        }

        .btn-modal {
          flex: 1;
          padding: 14px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s ease;
        }

        .btn-modal-dark {
          background: linear-gradient(135deg, #0369a1, #0284c7);
          color: white;
          box-shadow: 0 4px 12px rgba(3, 105, 161, 0.25);
        }

        .btn-modal-dark:hover {
          background: linear-gradient(135deg, #0c4a6e, #0369a1);
        }

        .btn-modal-cancel {
          background: #F5F5F5;
          color: #666;
        }

        .btn-modal-cancel:hover {
          background: #E8E8E8;
        }

        /* Quick Actions Card */
        .quick-actions-card {
          background: #ffffff;
          border-radius: 14px;
          padding: 22px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.07);
        }

        /* Simplified Dashboard Overview Styles */
        .simple-overview {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .water-level-display {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 20px;
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border-radius: 12px;
          border: 1px solid #7dd3fc;
        }

        .water-icon {
          align-items: center;
          background: #ffffff;
          border: 1px solid #bae6fd;
          border-radius: 12px;
          display: flex;
          font-size: 34px;
          height: 58px;
          justify-content: center;
          line-height: 1;
          width: 58px;
        }

        .water-info {
          flex: 1;
        }

        .water-value-large {
          font-size: 38px;
          font-weight: 800;
          color: #0369a1;
          letter-spacing: -1px;
          line-height: 1;
          margin-bottom: 4px;
        }

        .water-value-large .unit {
          font-size: 20px;
          margin-left: 8px;
          color: #0284c7;
          font-weight: 600;
        }

        .water-label-small {
          font-size: 13px;
          color: #0369a1;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .quick-info {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .info-item {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 14px;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          border: 1px solid #e2e8f0;
        }

        .info-label {
          font-size: 11px;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .info-value {
          font-size: 16px;
          color: #0f172a;
          font-weight: 700;
        }

        .btn-view-details {
          width: 100%;
          padding: 14px 18px;
          background: #0284c7;
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 800;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(3, 105, 161, 0.25);
        }

        .btn-view-details:hover {
          background: linear-gradient(135deg, #0c4a6e, #0369a1);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(3, 105, 161, 0.35);
        }

        .quick-actions-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .quick-action-btn {
          padding: 20px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          gap: 18px;
        }

        .quick-action-btn:hover {
          border-color: #0ea5e9;
          background: white;
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(14, 165, 233, 0.15);
        }

        .quick-action-icon {
          width: 46px;
          height: 46px;
          background: #0f172a;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 8px 16px rgba(3, 105, 161, 0.2);
        }

        .quick-action-text h4 {
          font-size: 14px;
          font-weight: 700;
          color: #000;
          margin-bottom: 2px;
        }

        .quick-action-text p {
          font-size: 12px;
          color: #666;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .hero-stats {
            flex-wrap: wrap;
          }
        }

        @media (max-width: 768px) {
          .dashboard-page {
            padding: 16px;
          }
          .dashboard-header {
            padding: 24px;
          }
          .hero-card {
            padding: 28px;
          }
          .hero-value {
            font-size: 42px;
          }
          .header-top {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }
          .stats-grid {
            grid-template-columns: 1fr;
          }
          .nodes-grid {
            grid-template-columns: 1fr;
          }
          .quick-actions-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Activation Modal */}
      {activationModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Activate {activationModal.name}</h2>
              <p className="modal-subtitle">Configure cable length to start monitoring</p>
            </div>

            <div className="modal-info">
              <p>
                <strong>Water Height Formula: H = h₁ - h₂</strong><br /><br />
                • <strong>h₁</strong> = Cable length (depth from surface to cable end)<br />
                • <strong>h₂</strong> = Sensor reading (depth to water surface)<br />
                • <strong>H</strong> = Water height above cable end<br /><br />
                ⚠️ If h₂ ≥ h₁, it means no water or sensor error<br />
                📏 Sensor must read LESS than cable length for water to be present
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">
                Cable Length (h₁) in meters <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={cableLength}
                onChange={(e) => setCableLength(e.target.value)}
                placeholder="Enter cable length, e.g., 50.5"
                className="form-input"
                autoFocus
              />
            </div>

            <div className="modal-actions">
              <button onClick={handleActivateNode} className="btn-modal btn-modal-dark">
                <CheckCircle size={16} />
                Activate Node
              </button>
              <button
                onClick={() => {
                  setActivationModal(null);
                  setCableLength("");
                }}
                className="btn-modal btn-modal-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Cable Length Modal */}
      {editModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Edit Cable Length - {editModal.name}</h2>
              <p className="modal-subtitle">Update h₁ (cable length) for this node</p>
            </div>

            <div className="modal-info">
              <p>
                <strong>Current Values:</strong><br />
                • h₁ (Cable): {editModal.h1_m}m<br />
                • h₂ (Sensor): {editModal.h2_m.toFixed(2)}m<br />
                • Water Height: {editModal.waterHeight}m<br /><br />
                Updating cable length will recalculate water height automatically.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">
                New Cable Length (h₁) in meters <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={cableLength}
                onChange={(e) => setCableLength(e.target.value)}
                placeholder="Enter new cable length"
                className="form-input"
                autoFocus
              />
            </div>

            <div className="modal-actions">
              <button onClick={handleUpdateCableLength} className="btn-modal btn-modal-dark">
                <CheckCircle size={16} />
                Update Cable Length
              </button>
              <button
                onClick={() => {
                  setEditModal(null);
                  setCableLength("");
                }}
                className="btn-modal btn-modal-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="dashboard-header">
        <div className="header-top">
          <div className="header-left">
            <div className="header-icon">
              <Droplets size={24} style={{ color: 'white' }} />
            </div>
            <div className="header-text">
              <h1>{greeting.text}</h1>
              <p>{greeting.subtext} • {stats.totalNodes} nodes monitoring • Last update: {lastUpdate.toLocaleTimeString()}</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn-header btn-secondary" onClick={() => navigate("/analytics")}>
              <BarChart3 size={16} />
              Analytics
            </button>
            <button className="btn-header btn-primary" onClick={() => window.location.reload()}>
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {/* Hero Card */}
        <div className="hero-card">
          <div className="hero-content">
            <div className="hero-label">Total Water Height</div>
            <div>
              <span className="hero-value"><AnimatedNumber value={stats.totalWaterHeight} decimals={1} suffix="" /></span>
              <span className="hero-unit">meters</span>
            </div>
            <div className="hero-subtitle">
              Average: {stats.avgWaterHeight}m across {stats.activeNodes} nodes sending data
            </div>
            <div className="hero-stats">
              <div className="hero-stat">
                <Droplets size={20} />
                <div>
                  <div className="hero-stat-label">Active Wells</div>
                  <div className="hero-stat-value">{stats.activeNodes}</div>
                </div>
              </div>
              <div className="hero-stat">
                <AlertTriangle size={20} />
                <div>
                  <div className="hero-stat-label">Alerts</div>
                  <div className="hero-stat-value">{stats.alerts}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <Droplets size={20} />
            </div>
          </div>
          <p className="stat-label">Total Nodes</p>
          <p className="stat-value">{stats.totalNodes}</p>
          <p className="stat-subtitle">LoRa Sensors</p>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon" style={{ background: '#DCFCE7' }}>
              <CheckCircle size={20} style={{ color: '#16A34A' }} />
            </div>
          </div>
          <p className="stat-label">Active Nodes</p>
          <p className="stat-value">{stats.activeNodes}</p>
          <p className="stat-subtitle">Sending data</p>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon" style={{ background: '#FEF3C7' }}>
              <AlertTriangle size={20} style={{ color: '#D97706' }} />
            </div>
          </div>
          <p className="stat-label">Alerts</p>
          <p className="stat-value">{stats.alerts}</p>
          <p className="stat-subtitle">Requires attention</p>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <XCircle size={20} />
            </div>
          </div>
          <p className="stat-label">Not Activated</p>
          <p className="stat-value">{stats.notActivated}</p>
          <p className="stat-subtitle">Pending setup</p>
        </div>
      </div>

      {/* Nodes Section */}
      <div className="section-header">
        <div className="section-title">
          <MapPin size={20} />
          <h2>Sensor Nodes</h2>
        </div>
        <div className="section-badge">
          {nodes.length} total
        </div>
      </div>

      <div className="nodes-grid">
        {nodes.map((node) => (
          <div key={node.id} className="node-card">
            <div className="node-header">
              <div className="node-info">
                <h3>{node.name}</h3>
                <div className="node-location">
                  <MapPin size={12} /> {node.location}, {node.region}
                </div>
              </div>
              <span
                className="status-badge"
                style={{
                  background: `${getStatusColor(node.status)}20`,
                  color: getStatusColor(node.status)
                }}
              >
                {getStatusIcon(node.status)}
                {node.status}
              </span>
            </div>

            {!node.activated ? (
              <div className="not-activated">
                <XCircle size={40} className="not-activated-icon" />
                <p className="not-activated-text">Node not yet activated</p>
                <p className="not-activated-count">{node.readingsCount} sensor readings available</p>
                <button
                  onClick={() => setActivationModal(node)}
                  className="btn btn-dark"
                  style={{ width: '100%' }}
                >
                  <CheckCircle size={16} />
                  Activate Node
                </button>
              </div>
            ) : (
              <>
                {/* Simplified Dashboard View - Quick Overview */}
                <div className="simple-overview">
                  {/* Water Level - Large and Prominent */}
                  <div className="water-level-display">
                    <div className="water-icon">💧</div>
                    <div className="water-info">
                      <div className="water-value-large">{node.waterHeight}<span className="unit">m</span></div>
                      <div className="water-label-small">Current Water Level</div>
                    </div>
                  </div>



                  {/* Quick Info */}
                  <div className="quick-info">
                    <div className="info-item">
                      <span className="info-label">Last Updated</span>
                      <span className="info-value">{node.lastUpdateDate && !isNaN(node.lastUpdateDate) ? node.lastUpdateDate.toLocaleTimeString() : 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Readings Today</span>
                      <span className="info-value">{node.readingsCount}</span>
                    </div>
                  </div>

                  {/* Single CTA Button */}
                  <button onClick={() => navigate('/monitor')} className="btn-view-details">
                    <Eye size={16} />
                    View Detailed Monitoring
                    <ChevronRight size={16} />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="section-header">
        <div className="section-title">
          <Sparkles size={20} />
          <h2>Quick Actions</h2>
        </div>
      </div>

      <div className="quick-actions-card">
        <div className="quick-actions-grid">
          <button className="quick-action-btn" onClick={() => navigate("/monitor")}>
            <div className="quick-action-icon">
              <Activity size={24} style={{ color: 'white' }} />
            </div>
            <div className="quick-action-text">
              <h4>Real-Time Monitor</h4>
              <p>View live sensor data</p>
            </div>
          </button>

          <button className="quick-action-btn" onClick={() => navigate("/analytics")}>
            <div className="quick-action-icon">
              <BarChart3 size={24} style={{ color: 'white' }} />
            </div>
            <div className="quick-action-text">
              <h4>Analytics</h4>
              <p>View trends & reports</p>
            </div>
          </button>

          <button className="quick-action-btn" onClick={() => navigate("/settings")}>
            <div className="quick-action-icon">
              <Settings size={24} style={{ color: 'white' }} />
            </div>
            <div className="quick-action-text">
              <h4>Settings</h4>
              <p>Configure system</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
